
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast"
import { useAdminAuth } from "@/contexts/AdminAuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, isLoading: authLoading } = useAdminAuth();

  // Redirect if already authenticated as admin
  useEffect(() => {
    if (authLoading) return;

    if (user && isAdmin) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user, isAdmin, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error("Email ou senha incorretos");
        return;
      }

      if (data.user) {
        const { data: isAdminUser, error: adminCheckError } = await supabase.rpc(
          "is_admin",
          { user_id: data.user.id },
        );

        if (adminCheckError) {
          await supabase.auth.signOut();
          toast.error("Erro ao validar permissões de administrador");
          return;
        }

        if (!isAdminUser) {
          await supabase.auth.signOut();
          toast.error("Acesso negado. Sua conta não é administradora.");
          navigate("/admin/access-denied", { replace: true });
          return;
        }

        toast.success("Login administrativo realizado com sucesso!");
        navigate("/admin/dashboard", { replace: true });
        return;
      }

      toast.error("Não foi possível concluir o login");
    } catch (error) {
      console.error("❌ AdminLogin: Exceção durante login:", error);
      toast.error("Erro ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="rounded-2xl shadow-xl border-border/60">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-violet-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Painel Administrativo
            </CardTitle>
            <CardDescription className="text-gray-600">
              Acesso restrito para administradores
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  placeholder="admin@exemplo.com"
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Senha
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10"
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 bg-violet-600 hover:bg-violet-700"
                disabled={isLoading}
              >
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
